import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useSnackbar } from 'notistack';
import { Box, Fade } from '@material-ui/core';
import { useAppContext } from 'contexts/AppContext';
import { isSuccessState, wait } from 'globals/utils';
import defaultLocales from 'globals/locales/default';
import FsPaper from 'ui-elements/main-elements/FsPaper';
import FsPendingButton from 'ui-elements/buttons/FsPendingButton';
import { observer } from 'mobx-react';
import FsDefaultFallbackComponent from 'ui-elements/loaders/FsDefaultFallbackComponent';
import {
  ButtonWrapperBox,
  InfoWrapperBox,
  Paragraph,
  Special,
  Strong,
  TrialBox,
} from './PlanDetails.style-elements';

const PlanDetails: React.FC = () => {
  const {
    isTrial,
    isSubscribed,
    setIsSubscribed,
    planDetails: { status, getPlanDetailsSnapshot, init: initStore },
  } = useAppContext();
  const {
    billingCycleOption,
    minimumSpending,
    perCustomer,
    totalCustomers,
    trialLeft,
  } = getPlanDetailsSnapshot();

  const { enqueueSnackbar } = useSnackbar();
  const firstUpdate = useRef(true);
  const [pending, isPending] = useState<boolean>(false);

  const clickHandler = async () => {
    isPending(true);
    await wait(1000);
    setIsSubscribed(!isSubscribed);
    isPending(false);
  };

  const triggerNotification = useCallback(() => {
    const variant = isSubscribed ? 'success' : 'warning';
    const currentState = isSubscribed ? 'subscribed' : 'unsubscribed';
    enqueueSnackbar(`You are ${currentState} now`, {
      variant,
      preventDuplicate: false,
    });
  }, [enqueueSnackbar, isSubscribed]);

  useLayoutEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
    } else {
      // like componentDidUpdateFunction
      // we avoid first trigger onDidMount, we only want to capture changes
      triggerNotification();
    }
  }, [isSubscribed, triggerNotification]);

  useEffect(() => {
    initStore();
  }, [initStore]);

  if (!isSuccessState(status))
    return (
      <FsDefaultFallbackComponent
        status={status}
        data-test="default-fallback-component"
      />
    );

  return (
    <Fade in timeout={1600}>
      <FsPaper
        title={defaultLocales.planDetails}
        alertContent={
          !isSubscribed ? defaultLocales.unsubscribedWarningInfo : undefined
        }
        severity="warning"
      >
        <Box>
          <Box marginBottom={2}>
            <Paragraph>
              {defaultLocales.selectedType}:{' '}
              {billingCycleOption && (
                <Strong component="span">
                  {defaultLocales.enums[billingCycleOption]}
                </Strong>
              )}
            </Paragraph>
          </Box>
          {isTrial && (
            <Box marginBottom={2}>
              <TrialBox>
                <Paragraph>
                  {defaultLocales.trial}:{' '}
                  <Special>
                    {trialLeft} {defaultLocales.daysLeft}
                  </Special>
                </Paragraph>
              </TrialBox>
            </Box>
          )}

          <InfoWrapperBox>
            <Paragraph>
              {defaultLocales.minimumSpending}:{' '}
              <Strong>{minimumSpending}</Strong>
            </Paragraph>
            <Paragraph>
              {defaultLocales.perCustomer}: <Strong>{perCustomer}</Strong>
            </Paragraph>
            <Paragraph>
              {defaultLocales.totalCustomers}: <Strong>{totalCustomers}</Strong>
            </Paragraph>
          </InfoWrapperBox>
          <ButtonWrapperBox>
            <FsPendingButton
              variant="outlined"
              color="primary"
              onClick={clickHandler}
              pending={pending}
              width={160}
            >
              {isSubscribed
                ? defaultLocales.unsubscribe
                : defaultLocales.subscribe}
            </FsPendingButton>
          </ButtonWrapperBox>
        </Box>
      </FsPaper>
    </Fade>
  );
};

export default observer(PlanDetails);
